/**
 * @fileoverview TourSection.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import FeaturedTourCard from './FeaturedTourCard'
import { FadeUp, ScaleIn } from '../../utils/motion'
import { apiFetch } from '../../utils/api'
import { useTranslation } from 'react-i18next'
import BookingProcessFlow from '../home/BookingProcessFlow'

function TourSection() {
    const scrollRef = useRef(null);
    const [tours, setTours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isBookingOpen, setIsBookingOpen] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        apiFetch('/tours?featured=true')
            .then(res => {
                if (res && res.data && res.data.length > 0) {
                    const formatted = res.data.map(t => ({
                        id: t._id,
                        slug: t.slug,
                        name: t.title,
                        category: t.categories?.[0] || 'Tour',
                        duration: t.duration,
                        price: t.price,
                        featuredIcons: t.featuredIcons || [],
                        image: (t.image && typeof t.image === 'object' && (t.image.l || t.image.h))
                            ? { l: t.image.l || t.image.h, h: t.image.h || t.image.l }
                            : { l: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' }
                    })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
                    setTours(formatted);
                } else {
                    setTours([]);
                }
            })
            .catch(() => {
                setTours([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current;
            const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
            scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
        }
    };

    return (
        <div className='w-full bg-white px-[5%] md:px-[10%] py-16 md:py-20 flex flex-col gap-6 md:gap-12 overflow-hidden'>
            <FadeUp className='flex flex-col lg:flex-row justify-between items-end gap-5'>
                <div className='flex flex-col gap-2 md:gap-4 text-left max-w-2xl'>
                    <div className='flex flex-col gap-3'>
                        <span className='text-KITH-primary font-bold tracking-widest uppercase text-[10px]'>{t('tours.subtitle')}</span>
                        <h2 className='text-xl md:text-3xl font-bold text-gray-800 leading-tight'>
                            {t('tours.title1')} <span className='text-KITH-primary'>{t('tours.title2')}</span>
                        </h2>
                    </div>
                    <p className='text-gray-500 text-xs md:text-sm leading-relaxed'>
                        {t('tours.desc')}
                    </p>
                    <button 
                        onClick={() => setIsBookingOpen(true)}
                        className='text-KITH-primary text-[10px] md:text-xs font-bold flex items-center gap-2 hover:gap-3 transition-all w-max mt-1 bg-KITH-primary/10 px-3 py-1.5 rounded-full hover:bg-KITH-primary hover:text-white uppercase tracking-wider'
                    >
                        Booking flow <i className="fi fi-br-arrow-right text-[8px] mt-0.5"></i>
                    </button>
                </div>

                <div className='flex gap-3'>
                    <button
                        onClick={() => scroll('left')}
                        aria-label="Previous tours"
                        className='w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-KITH-primary hover:text-white hover:border-KITH-primary transition-all shadow-sm'
                    >
                        <i className='fi fi-br-angle-left mt-1'></i>
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        aria-label="Next tours"
                        className='w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-KITH-primary hover:text-white hover:border-KITH-primary transition-all shadow-sm'
                    >
                        <i className='fi fi-br-angle-right mt-1'></i>
                    </button>
                </div>
            </FadeUp>

            {/* Featured Tours Carousel */}
            <div
                ref={scrollRef}
                className='w-full flex gap-8 overflow-x-scroll pb-12 pt-4 scroll-smooth scrollbarHide snap-x snap-mandatory min-h-[300px] items-center'
            >
                {loading ? (
                    <div className='flex items-center justify-center w-full h-[373px] md:h-[427px] lg:h-[467px]'>
                        <div className='w-10 h-10 border-4 border-KITH-primary/30 border-t-KITH-primary rounded-full animate-spin'></div>
                    </div>
                ) : tours.length > 0 ? (
                    tours.map((tour, index) => (
                        <div key={index} className='snap-start shrink-0 w-[260px] md:w-[280px] lg:w-[300px]'>
                            <FeaturedTourCard tour={tour} />
                        </div>
                    ))
                ) : (
                    <div className='flex items-center justify-center py-10 w-full text-gray-400 font-bold text-sm'>
                        {t('tours.no_featured')}
                    </div>
                )}

                {!loading && (
                    <Link to="/tour-plans" className='snap-start shrink-0 w-[260px] md:w-[280px] lg:w-[300px] aspect-[3/4] bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-4 group hover:border-KITH-primary transition-all shadow-sm'>
                        <div className='w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md group-hover:bg-KITH-primary group-hover:text-white transition-all'>
                            <i className='fi fi-br-plus text-xl'></i>
                        </div>
                        <span className='font-bold text-gray-400 group-hover:text-KITH-primary'>{t('tours.explore_more')}</span>
                    </Link>
                )}
            </div>

            <ScaleIn>
                <div className='mt-8 max-w-5xl mx-auto w-full bg-KITH-primary rounded-[2.5rem] p-8 md:p-12 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 group hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer'>
                    <div className='absolute top-0 right-0 w-1/2 h-full bg-white/5 skew-x-[-20deg] translate-x-20 transition-transform duration-1000 group-hover:translate-x-5'></div>

                    <div className='flex flex-col gap-3 text-left z-10'>
                        <h3 className='text-xl md:text-3xl font-bold text-white leading-tight'>
                            {t('tours.custom_title1')} <br />
                            <span className='opacity-80'>{t('tours.custom_title2')}</span>
                        </h3>
                        <p className='text-white/70 max-w-md text-xs md:text-sm'>
                            {t('tours.custom_desc')}
                        </p>
                    </div>

                    <Link to="/custom-tour" className='px-10 py-4 bg-white text-KITH-primary font-bold rounded-xl hover:bg-gray-100 transition-all shadow-xl group-hover:scale-105 z-10 text-sm md:text-base shrink-0'>
                        {t('tours.start_planning')}
                    </Link>
                </div>
            </ScaleIn>
            
            <BookingProcessFlow isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
        </div>
    )
}

export default TourSection